import { Box } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { ReactNode, useMemo, useCallback } from 'react';
import _uniq from 'lodash/uniq';
import { Issue } from './issuesTypes';
import { resourceTypeToLabel } from './issuesHelper';
import capitalize from 'lodash/capitalize';
import { IssueStatusValues } from '@server/issues';
import { FiltersRenderer } from '../common/filters/filters-renderer/FiltersRenderer';
import {
  FilterComponentType,
  Filters,
  FilterValues,
} from '../common/filters/filters-renderer/types';

export type IssuesFiltersType = {
  selectedConfigurationTypes: string[];
  selectedIssues: string[];
  selectedSeverity: string[];
  selectedStatus: string[];
};

const getFiltersInitialState = (): IssuesFiltersType => ({
  selectedConfigurationTypes: [],
  selectedIssues: [],
  selectedSeverity: [],
  selectedStatus: ['open'],
});

const IssuesFilters = ({
  filters,
  setFilter,
  issues,
  searchJsx,
}: {
  filters: IssuesFiltersType;
  setFilter: (filter: Partial<IssuesFiltersType>) => void;
  issues: Issue[];
  searchJsx: ReactNode;
}) => {
  const filtersConfig: Filters = useMemo(() => {
    const uniqueTypes = _uniq(issues.map((issue) => issue.resourceType));
    const configurationTypesItems = uniqueTypes.map((type) => ({
      value: type,
      label: resourceTypeToLabel[type] || type,
    }));

    const uniqueIssues = _uniq(issues.map((issue) => issue.bestPracticeName));
    const issuesItems = uniqueIssues.map((issue) => ({
      value: issue,
      label: issue,
    }));

    const uniqueSeverity = _uniq(issues.map((issue) => issue.severity));
    const severityItems = uniqueSeverity.map((severity) => ({
      value: severity,
      label: severity,
    }));

    const statusItems = IssueStatusValues.map((status) => ({
      value: status,
      label: capitalize(status),
    }));

    return {
      selectedConfigurationTypes: {
        type: FilterComponentType.MULTI_SELECT,
        label: 'Type',
        items: configurationTypesItems,
      },
      selectedIssues: {
        type: FilterComponentType.MULTI_SELECT,
        label: 'Issue',
        items: issuesItems,
      },
      selectedSeverity: {
        type: FilterComponentType.MULTI_SELECT,
        label: 'Impact',
        items: severityItems,
      },
      selectedStatus: {
        type: FilterComponentType.MULTI_SELECT,
        label: 'Status',
        items: statusItems,
      },
    };
  }, [issues]);

  // Convert IssuesFiltersType to FilterValues format for FiltersRenderer
  const filterValues: FilterValues = useMemo(
    () => ({
      selectedConfigurationTypes: { selectedValues: filters.selectedConfigurationTypes },
      selectedIssues: { selectedValues: filters.selectedIssues },
      selectedSeverity: { selectedValues: filters.selectedSeverity },
      selectedStatus: { selectedValues: filters.selectedStatus },
    }),
    [filters],
  );

  const handleFilterChange = useCallback(
    (filterKey: string, values: string[]) => {
      setFilter({ [filterKey]: values });
    },
    [setFilter],
  );

  const handleClearAll = useCallback(() => {
    setFilter(getFiltersInitialState());
  }, [setFilter]);

  // Custom check for active filters (treating 'open' as default for status)
  const checkFiltersHaveValues = useCallback(() => {
    const hasConfigTypes = filters.selectedConfigurationTypes.length > 0;
    const hasIssues = filters.selectedIssues.length > 0;
    const hasSeverity = filters.selectedSeverity.length > 0;
    // Status: 'open' is the default, so only show clear if different
    const hasNonDefaultStatus =
      filters.selectedStatus.length > 0 &&
      !(filters.selectedStatus.length === 1 && filters.selectedStatus[0] === 'open');

    return hasConfigTypes || hasIssues || hasSeverity || hasNonDefaultStatus;
  }, [filters]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        backgroundColor: colors.grey[100],
        borderRadius: '8px',
        padding: '8px 12px',
      }}
    >
      <FiltersRenderer
        filters={filtersConfig}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
        withPanelWrapper={true}
        checkFiltersHaveValuesCb={checkFiltersHaveValues}
        anchorRight={true}
      />
      <Box width="200px" flexShrink={0}>
        {searchJsx}
      </Box>
    </Box>
  );
};

export { getFiltersInitialState };
export default IssuesFilters;
